<template>
  <div class="ccs-input" :class="{
      ...modifierClasses,
      'ccs-input--error': !isValid && error && !readOnly,
      'ccs-input--active': isInputStateActive,
      'ccs-input--required': required,
      'ccs-input--time': type === 'time',
      'ccs-input--file': type === 'file',
      'ccs-input--file_deletable': type === 'file' && !!this.rawVal && this.allowFileDelete,
      'ccs-input--file_uploaded': type === 'file' && !!this.rawVal,
    }" :read-only="readOnly" :disabled="disabled" :data-error="error">

    <!-- Text -->
    <input class="ccs-input__field"
      v-if="type === 'text' || type === 'password'"
      :type="type" :placeholder="placeholder" v-model="rawVal"
      :readonly="readOnly" :disabled="disabled"
      @focus="onFocus" @blur="onUnfocus">

    <!-- Number -->
    <input class="ccs-input__field" v-if="$props.type === 'number'"
      :type="$props.type" :placeholder="placeholder" v-model="rawVal"
      :readonly="readOnly" :disabled="disabled"
      @focus="onFocus" @blur="onUnfocus">

    <!-- File -->
    <input class="ccs-input__field" v-if="$props.type === 'file'" :id="_uid"
      :type="$props.type" :placeholder="$props.placeholder"
      :readonly="$props.readOnly" :disabled="$props.disabled"
      @change="onFileChange" :accept="$props.accept">

    <!-- Date -->
    <VueDatePicker class="ccs-input__field" v-if="$props.type === 'date'"
      v-model="rawVal" :disabled="!isInputAllowed" noHeader noCalendarIcon/>

    <!-- Time -->
    <vue-timepicker class="ccs-input__field" input-class="input-time_field"
      v-if="$props.type === 'time'" v-model="rawVal" format="HH:mm"
      @open="onFocus" @close="onUnfocus"/>

    <!-- Textarea -->
    <textarea ref="textarea" class="ccs-input__field" v-if="$props.type === 'textarea'" type="text"
      :placeholder="$props.placeholder" v-model="rawVal"
      :readonly="$props.readOnly" :disabled="$props.disabled"
      :style="textareaStyles" @focus="onFocus" @blur="onUnfocus">
    </textarea>

    <!-- Dropdown -->
    <ccs-dropdown v-if="type === 'dropdown'" v-model="rawVal" v-bind="$props"
      @focus="onFocus" @unfocus="onUnfocus" @select="onSelect"/>

    <!-- Autocomplete -->
    <ccs-autocomplete v-if="type === 'autocomplete'" v-model="rawVal" v-bind="$props"
      @focus="onFocus" @unfocus="onUnfocus" @select="onSelect"
      @change="(change) => $emit('change', change)"/>

    <label class="ccs-input__label" v-if="$props.label || $props.type === 'file'" :for="_uid">
      {{ $props.label || '' }}
    </label>

    <!-- Icons & Actions -->
    <slot :data="$props" v-if="!(this.parsedModifiers.active_only && !this.isInputStateActive)">

      <!-- Render image if in props -->
      <v-icon class="ccs-input__icon" v-if="$props.iconName" v-show="isInputAllowed"
        :name="iconName"/>

      <!-- Render only alt text -->
      <div class="ccs-input__icon" v-if="!$props.iconName && $props.imgAlt" v-show="isInputAllowed">
        {{ $props.imgAlt }}
      </div>

      <!-- Calendar (for `date` only)-->
      <v-icon class="ccs-input__icon" v-if="$props.type === 'date'" v-show="isInputAllowed"
        name="calendar-alt"/>

      <!-- Arrows (for `number` only) -->
      <img class="ccs-input__arrow-up" v-if="$props.type === 'number'" @click="increaseVal"
        src="@/assets/icons/dropdown_arrow.svg" alt="" v-show="isInputAllowed">
      <img class="ccs-input__arrow-down" v-if="$props.type === 'number'" @click="decreaseVal"
        src="@/assets/icons/dropdown_arrow.svg" alt="" v-show="isInputAllowed">

      <!-- File Delete (for `file` only) -->
      <v-icon v-if="$props.allowFileDelete"
        class="ccs-input__icon ccs-icon--secondary ccs-icon--hover" name="trash" scale="0.9"
        @click="resetFile()"/>
<!--      <v-icon v-if="$props.allowFileDelete && !!this.rawVal"
        class="ccs-input__icon ccs-icon&#45;&#45;secondary ccs-icon&#45;&#45;hover" name="trash" scale="0.9"
        @click="resetFile()"/>-->

    </slot>
  </div>
</template>

<script>
import VueTimepicker from 'vue2-timepicker/src/vue-timepicker.vue';

import CcsAutocomplete from '@/components/forms/CcsAutocomplete.vue';
import CcsDropdown from '@/components/forms/CcsDropdown.vue';

const InputStates = {
  default: 0,
  active: 1,
};

export default {
  name: 'Input',
  components: {
    VueTimepicker,
    CcsDropdown,
    CcsAutocomplete,
  },
  props: {
    value: {
      required: true,
    },
    type: {
      required: true,
      type: String,
    },
    isValid: {
      type: Boolean,
      default: true,
    },
    error: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    iconName: {
      type: String,
      default: '',
    },
    imgAlt: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    accept: {
      type: String,
      default: '',
    },
    allowFileDelete: {
      type: Boolean,
      default: false,
    },
    required: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => [],
    },
    modifiers: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      InputStates,
      inputState: InputStates.default,
      isMounted: false,
    };
  },
  computed: {
    textareaStyles() {
      return {
        'resize': (this.$props.disabled || this.$props.readOnly ||
          (this.parsedModifiers.active_only && !this.isInputStateActive)) ? 'none' : 'vertical',
        'overflow': this.isMounted && this.parsedModifiers.auto_expand &&
          !(this.parsedModifiers.active_only && this.isInputStateActive) ? 'hidden' : 'auto',
      };
    },
    rawVal: {
      set(change) {
        this.$emit('input', change);
      },
      get() {
        return this.$props.value;
      }
    },
    isInputStateActive() {
      return this.inputState === InputStates.active;
    },
    isInputAllowed() {
      return !this.$props.disabled && !this.$props.readOnly;
    },
    parsedModifiers() {
      return this.$props.modifiers.reduce((result, modifier) => {
        const parsedModifier = {};
        parsedModifier[modifier] = true;
        return Object.assign({}, result, parsedModifier);
      }, {});
    },
    modifierClasses() {
      return {
        'ccs-input--zero_label': this.parsedModifiers.zero_label,
        'ccs-input--big': this.parsedModifiers.big,
        'ccs-input--active_only':  this.parsedModifiers.active_only && !this.isInputStateActive,
        'ccs-input--minimal':  this.parsedModifiers.minimal,
      };
    },
  },
  methods: {
    onFocus() {
      this.setInputState(InputStates.active);
      this.$emit('focus');
      if (!this.isValid && this.error) {
        this.$emit('error', '');
      }
    },
    onUnfocus() {
      this.setInputState(InputStates.default);
      this.$emit('unfocus');
    },
    onFileChange(e) {
      const files = e.target.files || e.dataTransfer.files;
      if (!files.length) return;
      if (!this.isValid && this.error) {
        this.$emit('error', '');
      }
      this.$emit('input', files[0]);
    },
    resetFile() {
      this.$emit('input', null);
    },
    setInputState(state) {
      this.inputState = state;
      setTimeout(this.resetTextareaScrollAndHeight, 200);
    },
    resetTextareaScrollAndHeight() {
      if (this.inputState === InputStates.default &&
        this.$props.type === 'textarea' &&
        !!this.$refs.textarea) {
        this.$refs.textarea.scrollTop = 0;
        this.$refs.textarea.style.height = `${this.$refs.textarea.scrollHeight + 2}px`;
      }
    },
    onSelect(selectedItem) {
      this.$emit('select', selectedItem);
    },
    increaseVal() {
      if (!this.$props.disabled) this.rawVal += 1;
    },
    decreaseVal() {
      if (!this.$props.disabled) this.rawVal -= 1;
    },
  },
  mounted() {
    this.isMounted = true;
    if (this.parsedModifiers.auto_expand) {
      this.resetTextareaScrollAndHeight()
    }
  }
}
</script>

<style lang="scss" scoped>
.ccs-input {
  position: relative;
  min-height: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  border-radius: 5px;

  .ccs-input__label {
    min-height: 12px;
    width: 100%;
    margin-bottom: 10px;
    order: -1;
    font-size: 12px;
    font-weight: bold;
    color: $surfaceDarker;
  }

  .ccs-input__field {
    box-sizing: border-box;
    padding: 11px 40px 11px 15px;
    max-width: 100%;
    flex: 1;
    font-size: 16px;
    background: $neutralLighter;
    border: 1px solid $neutralLighter;
    border-radius: 5px;
    transition: border     ease-in-out .2s,
                padding    ease-in-out .2s,
                background ease-in-out .2s;

    &:disabled {
      cursor: not-allowed;
    }

    &::placeholder {
      color: $neutralDarker;
    }

  }

  textarea.ccs-input__field {
    @include scroll-view('auto');
  }

  &__icon {
    z-index: 90;
    padding: 0 10px;
    margin-left: -50px;
    color: $primary;
    font-size: 12px;
    font-weight: bold;
  }

  &__field[type="number"] {
    appearance: textfield;

    ~ .ccs-input__arrow-up {
      padding: 0 10px;
      margin-top: -15px;
      margin-left: -50px;
      transform: rotate(180deg);

      &:hover {
        cursor: pointer;
      }

    }

    ~ .ccs-input__arrow-down {
      padding: 0 10px;
      margin-bottom: -15px;
      margin-left: -50px;

      &:hover {
        cursor: pointer;
      }

    }

  }

  &[read-only = "true"] {

    .ccs-input__field {
      padding: 0;
      background: #fff;
      border: 1px solid #fff;

      &:hover {
        cursor: default;
      }

    }

    &:hover {
      cursor: default;
    }

  }

  &[disabled = "disabled"] {

    .ccs-input__field {
      &:hover {
        cursor: not-allowed;
      }

    }

    &:hover {
      cursor: not-allowed;
    }

  }

  &--file {

    .ccs-input__field {
      padding: 7px 15px;
      height: 43px;
      opacity: 0;
    }

    .ccs-input__label {
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;

      &:before {
        content: 'Прикрепить файл';
        box-sizing: border-box;
        position: absolute;
        bottom: 0;
        left: 0;
        width: 100%;
        height: calc(100% - 22px);
        display: flex;
        justify-content: center;
        align-items: center;
        color: $primaryLightest;
        background: #fff;
        font-size: 14px;
        font-weight: bold;
        border: 1px solid $primaryLightest;
        border-radius: 5px;
        transition: all ease-in-out .2s;
      }

      &:hover:before {
        color: $primary;
        background: $neutralLightest;
        cursor: pointer;
      }

    }

  }

  &--file_deletable {

    .ccs-input__field {
      padding: 11px 40px 11px 15px;
    }

    .ccs-input__label {
      width: calc(100% - 30px);

      &:before {
        content: 'Изменить файл';
      }

    }

  }

  &--file_uploaded {

   .ccs-input__label {

      &:before {
        content: 'Изменить файл';
      }

    }

  }

  &--active_only {

    .ccs-input__field {
      padding: 0;
      background: #fff;
      border: 1px solid #fff;
    }

    textarea.ccs-input__field {
      padding-right: 55px;
    }

  }

  &--minimal {
    @extend .ccs-input--active_only;
  }

  &--minimal.ccs-input--active {

    .ccs-input__field {
      background: #fff;
      border: 1px solid #fff;
    }

  }

  &--error {

    ::v-deep.ccs-input__field, ::v-deep.dropdown {
      background: $neutral;
      border: 1px solid $secondary;
    }

    &:before {
      content: attr(data-error);
      position: absolute;
      bottom: -11px;
      max-width: 100%;
      color: $secondary;
      font-size: 10px;
      white-space: nowrap;
      text-overflow: ellipsis;
    }

    &.ccs-input--file {

      &:before {
        bottom: 6px;
      }

      .ccs-input__label {

        &:before {
          color: $secondary;
          background: #fff;
          border: 1px solid $secondary;
        }

      }

    }

    &.ccs-input--time {

      .ccs-input__field {

        ::v-deep.input-time_field {
          background: #fff;
        }

      }

    }

  }

  &--active {

    .ccs-input__field {
      background: #fff;
      border: 1px solid $primaryLightest;

      ::v-deep.input-time_field {
        background: #fff;
      }

    }

  }

  &--required {

    .ccs-input__label {

      &:after {
        content: '*';
        color: $secondary;
      }

    }

  }

  .ccs-input__field[type="number"]::-webkit-inner-spin-button,
  .ccs-input__field[type="number"]::-webkit-outer-spin-button {
    -webkit-appearance: none;
  }

  &--big {

    .ccs-input__field {
      font-size: 24px;
    }

  }

  &--zero_label {

    .ccs-input__label {
      margin-bottom: 0;
    }

  }

  &.ccs-input--zero_label.ccs-input--file {

    .ccs-input__label:before {
      left: 0;
      height: 100%;
    }

  }

}
</style>
